/**
 * Frontend focus/visibility monitor. It only produces evidence records; it never
 * blocks, locks or submits anything. See docs/ANTI-CHEAT-FRONTEND.md.
 */

export type AwaySignal = 'hidden' | 'blur' | 'pagehide' | 'fullscreen_exit' | 'focus_poll'

export interface IntegrityEvent {
  id: string
  /** First signal that opened the episode. */
  kind: AwaySignal
  /** All signals seen during the episode (deduplicated). */
  signals: AwaySignal[]
  /** Device clock, epoch ms. */
  started_at: number
  /** Device clock plus server offset (epoch ms), null when the offset is unknown. */
  started_at_server: number | null
  /** Milliseconds away; null while the episode is still open. */
  duration_ms: number | null
  returned: boolean
}

export interface FocusMonitorDeps {
  doc: Pick<Document, 'addEventListener' | 'removeEventListener' | 'hasFocus' | 'visibilityState' | 'fullscreenElement'>
  win: Pick<Window, 'addEventListener' | 'removeEventListener'>
  now: () => number
  setInterval: (fn: () => void, ms: number) => unknown
  clearInterval: (handle: unknown) => void
  /** Server clock minus device clock in ms, if known. */
  getServerOffset?: () => number | null
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  storageKey: string
  pollMs?: number
  maxEvents?: number
  /** Episodes shorter than this are dropped as noise (notification shade etc). */
  minDurationMs?: number
  onChange?: (events: IntegrityEvent[], current: IntegrityEvent | null) => void
  /** Called when an episode ends and the page is back. */
  onReturn?: (event: IntegrityEvent) => void
}

const PREFIX = 'oap-exp1/integrity/'
export const DEFAULT_MAX_EVENTS = 200

export function integrityStorageKey(examId: string, attemptId: string): string {
  return `${PREFIX}${encodeURIComponent(examId)}/${encodeURIComponent(attemptId)}`
}

export function parseIntegrityEvents(raw: string | null): IntegrityEvent[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((value): value is IntegrityEvent => {
      if (!value || typeof value !== 'object') return false
      const c = value as Record<string, unknown>
      return typeof c.id === 'string' && typeof c.kind === 'string' && typeof c.started_at === 'number'
    })
  } catch {
    return []
  }
}

export function loadIntegrityEvents(
  storage: Pick<Storage, 'getItem'> | null | undefined,
  key: string,
): IntegrityEvent[] {
  try {
    return parseIntegrityEvents(storage ? storage.getItem(key) : null)
  } catch {
    return []
  }
}

export function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export class FocusMonitor {
  private readonly deps: FocusMonitorDeps
  private events: IntegrityEvent[]
  private current: IntegrityEvent | null = null
  private active = new Set<AwaySignal>()
  private timer: unknown = null
  private running = false
  private wasFullscreen = false
  private seq = 0

  constructor(deps: FocusMonitorDeps) {
    this.deps = deps
    this.events = loadIntegrityEvents(deps.storage, deps.storageKey)
  }

  getEvents(): IntegrityEvent[] {
    return this.events.slice()
  }

  getCurrent(): IntegrityEvent | null {
    return this.current
  }

  start(): void {
    if (this.running) return
    this.running = true
    const { doc, win } = this.deps
    doc.addEventListener('visibilitychange', this.onVisibility)
    win.addEventListener('blur', this.onBlur)
    win.addEventListener('focus', this.onFocus)
    win.addEventListener('pagehide', this.onPageHide)
    win.addEventListener('pageshow', this.onPageShow)
    doc.addEventListener('fullscreenchange', this.onFullscreen)
    this.wasFullscreen = Boolean(doc.fullscreenElement)
    this.timer = this.deps.setInterval(this.poll, this.deps.pollMs ?? 1000)
    if (doc.visibilityState === 'hidden') this.raise('hidden')
    else if (!doc.hasFocus()) this.raise('focus_poll')
  }

  /** Stops all listeners and timers. An open episode stays recorded as not returned. */
  stop(): void {
    if (!this.running) return
    this.running = false
    const { doc, win } = this.deps
    doc.removeEventListener('visibilitychange', this.onVisibility)
    win.removeEventListener('blur', this.onBlur)
    win.removeEventListener('focus', this.onFocus)
    win.removeEventListener('pagehide', this.onPageHide)
    win.removeEventListener('pageshow', this.onPageShow)
    doc.removeEventListener('fullscreenchange', this.onFullscreen)
    if (this.timer !== null) this.deps.clearInterval(this.timer)
    this.timer = null
    this.active.clear()
    this.current = null
    this.persist()
  }

  private readonly onVisibility = (): void => {
    if (this.deps.doc.visibilityState === 'hidden') this.raise('hidden')
    else this.clear('hidden')
  }
  private readonly onBlur = (): void => this.raise('blur')
  private readonly onFocus = (): void => this.clear('blur', 'focus_poll')
  private readonly onPageHide = (): void => this.raise('pagehide')
  private readonly onPageShow = (): void => this.clear('pagehide')

  private readonly onFullscreen = (): void => {
    const now = Boolean(this.deps.doc.fullscreenElement)
    if (this.wasFullscreen && !now) this.raise('fullscreen_exit')
    else if (now) this.clear('fullscreen_exit')
    this.wasFullscreen = now
  }

  private readonly poll = (): void => {
    if (!this.running) return
    const { doc } = this.deps
    if (!doc.hasFocus()) {
      if (!this.active.has('blur') && !this.active.has('focus_poll')) this.raise('focus_poll')
      return
    }
    // Focus is back: clear stale blur state and the point-in-time fullscreen signal.
    this.clear('blur', 'focus_poll')
    if (doc.visibilityState !== 'hidden') this.clear('fullscreen_exit')
  }

  private raise(signal: AwaySignal): void {
    if (!this.running) return
    const wasAway = this.active.size > 0
    this.active.add(signal)
    if (!wasAway) {
      const startedAt = this.deps.now()
      const offset = this.deps.getServerOffset?.() ?? null
      this.seq += 1
      this.current = {
        id: `${startedAt}-${this.seq}`,
        kind: signal,
        signals: [signal],
        started_at: startedAt,
        started_at_server: offset === null ? null : startedAt + offset,
        duration_ms: null,
        returned: false,
      }
      this.events.push(this.current)
      this.trim()
    } else if (this.current && !this.current.signals.includes(signal)) {
      this.current.signals.push(signal)
    }
    this.persist()
  }

  private clear(...signals: AwaySignal[]): void {
    if (!this.running || !this.current) return
    let changed = false
    for (const signal of signals) changed = this.active.delete(signal) || changed
    if (!changed || this.active.size > 0) return
    const episode = this.current
    this.current = null
    episode.duration_ms = Math.max(0, this.deps.now() - episode.started_at)
    episode.returned = true
    if (episode.duration_ms < (this.deps.minDurationMs ?? 1000)) {
      this.events = this.events.filter((event) => event !== episode)
      this.persist()
      return
    }
    this.persist()
    this.deps.onReturn?.(episode)
  }

  private trim(): void {
    const max = this.deps.maxEvents ?? DEFAULT_MAX_EVENTS
    if (this.events.length > max) this.events = this.events.slice(this.events.length - max)
  }

  private persist(): void {
    try {
      this.deps.storage?.setItem(this.deps.storageKey, JSON.stringify(this.events))
    } catch {
      // Storage may be unavailable or full; the in-memory queue still works.
    }
    this.deps.onChange?.(this.getEvents(), this.current)
  }
}

/**
 * Isolated sender for integrity events.
 * NOTE: the server contract does not exist yet (owner decision pending), so this
 * only persists locally and performs NO network call. Do not add a URL here until
 * an endpoint is agreed. See docs/ANTI-CHEAT-FRONTEND.md, "Kontrak yang diusulkan".
 */
export function flushIntegrityEvents(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  key: string,
  events: IntegrityEvent[],
): void {
  try {
    storage?.setItem(key, JSON.stringify(events))
  } catch {
    // Local-only for now.
  }
}

export function createFocusMonitor(
  options: Pick<FocusMonitorDeps, 'storageKey' | 'getServerOffset' | 'onChange' | 'onReturn'>,
): FocusMonitor {
  return new FocusMonitor({
    ...options,
    doc: document,
    win: window,
    now: () => Date.now(),
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (handle) => window.clearInterval(handle as number),
    storage: safeStorage(),
  })
}
